import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { HttpCacheManager } from '@ngneat/cashew';
import { BaseHttpService } from './base-http.service';
import {
  PriceManagementLogResponse,
  PriceManagementLog,
  LogListItem,
  PriceManagementSnapshot
} from '../interfaces/price-management-log.interface';

const expandParams = {
  'expand[0]': 'createdBy',
  'expand[1]': 'updatedBy'
};

@Injectable({ providedIn: 'root' })
export class PriceManagementLogService extends BaseHttpService {
  override cacheEnabled = false;

  // Felder die NICHT verglichen werden sollen
  private readonly EXCLUDED_FIELDS: Set<string> = new Set([
    'id',
    'created_at',
    'updated_at',
    'created_by',
    'updated_by',
    'deleted_at',
    'price_management_key',
    'visible_for_sug',
    'visible_for_ems',
    'objection_possible_until',
    'advertising_categories_string',
    'agreementValidUntilInitially'
  ]);

  // Arrays die besonders behandelt werden
  private readonly ARRAY_FIELDS: Set<string> = new Set([
    'companies',
    'media',
    'calculation_parameters',
    'price_management_weight_sets',
    'advertisingTypes',
    'companyPriceManagements'
  ]);

  // Felder die IMMER einen tiefen JSON-Vergleich benötigen
  private readonly DEEP_COMPARE_FIELDS: Set<string> = new Set([
    'price_management_weight_sets',  // Wegen verschachtelter weight_ranges und surcharges
    'calculation_parameters'          // Wegen verschachtelter company_price_managements
  ]);

  constructor(
    http: HttpClient,
    cookieService: CookieService,
    manager: HttpCacheManager,
  ) {
    super(http, cookieService, manager);
  }

  getConditionLogs(conditionId: number, params?: any): Observable<PriceManagementLogResponse> {
    const queryParams: Record<string, string> = {
      ...expandParams,
      ...params,
      'filter[price_management_id][eq]': conditionId.toString()
    };

    return this.get('price-management-logs', queryParams);
  }

  getConditionLogsList(conditionId: number, params?: any): Observable<PriceManagementLog[]> {
    const queryParams: Record<string, string> = {
      ...expandParams,
      ...params,
      'filter[price_management_id][eq]': conditionId.toString()
    };

    return this.getAll('price-management-logs', queryParams);
  }

  buildLogList(logs: PriceManagementLog[]): LogListItem[] {
    return logs.map((log: PriceManagementLog): LogListItem => ({
      logId: log.id,
      timestamp: log.updated_at,
      user: log.updatedBy,
      snapshot: this.parseSnapshot(log.value)
    })).sort((a: LogListItem, b: LogListItem): number =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  parseSnapshot(valueJson: string): PriceManagementSnapshot {
    try {
      return JSON.parse(valueJson) as PriceManagementSnapshot;
    } catch (error) {
      console.error('Error parsing snapshot:', error);
      return {} as PriceManagementSnapshot;
    }
  }

  /**
   * Vergleicht zwei Snapshots intelligent
   */
  compareSnapshots(older: PriceManagementSnapshot, newer: PriceManagementSnapshot): string[] {
    const changedFields: string[] = [];

    const allKeys: Set<string> = new Set([
      ...Object.keys(older || {}),
      ...Object.keys(newer || {})
    ]);

    allKeys.forEach((key: string): void => {
      if (this.EXCLUDED_FIELDS.has(key)) {
        return;
      }

      const oldValue: any = older[key];
      const newValue: any = newer[key];

      // Spezielle Behandlung für Arrays
      if (this.ARRAY_FIELDS.has(key)) {
        if (this.hasArrayChanged(oldValue, newValue, key)) {  // ✅ key mitgeben
          changedFields.push(key);
        }
        return;
      }

      if (this.hasValueChanged(oldValue, newValue)) {
        changedFields.push(key);
      }
    });

    return changedFields;
  }

  /**
   * Prüft ob ein Array-Feld sich geändert hat
   */
  private hasArrayChanged(
    oldArray: any[] | undefined | null,
    newArray: any[] | undefined | null,
    fieldName?: string  // ✅ NEU: Feldname für spezielle Behandlung
  ): boolean {
    if (!oldArray && !newArray) {
      return false;
    }

    if (!oldArray || !newArray) {
      return true;
    }

    if (oldArray.length !== newArray.length) {
      return true;
    }

    if (oldArray.length === 0) {
      return false;
    }

    // Für verschachtelte Strukturen IMMER tiefer Vergleich
    if (fieldName && this.DEEP_COMPARE_FIELDS.has(fieldName)) {
      return JSON.stringify(oldArray) !== JSON.stringify(newArray);
    }

    // Für Relationen: NUR auf IDs prüfen
    const hasIds = oldArray.length > 0 && oldArray[0]?.id !== undefined;

    if (hasIds) {
      const oldIds = oldArray.map(item => item.id).sort();
      const newIds = newArray.map(item => item.id).sort();
      return JSON.stringify(oldIds) !== JSON.stringify(newIds);
    }

    // Für Arrays ohne IDs: Detaillierter Vergleich nur bei kleinen Arrays
    if (oldArray.length <= 10) {
      return JSON.stringify(oldArray) !== JSON.stringify(newArray);
    }

    return false;
  }

  /**
   * Prüft ob ein normaler Wert sich geändert hat
   */
  private hasValueChanged(oldValue: any, newValue: any): boolean {
    if (oldValue == null && newValue == null) {
      return false;
    }

    if (oldValue == null || newValue == null) {
      return true;
    }

    // ✅ NEU: Für Objekte - entferne Timestamp-Felder vor Vergleich
    if (typeof oldValue === 'object' && typeof newValue === 'object' && !Array.isArray(oldValue)) {
      const cleanOld = this.removeTimestampsFromObject(oldValue);
      const cleanNew = this.removeTimestampsFromObject(newValue);
      return JSON.stringify(cleanOld) !== JSON.stringify(cleanNew);
    }

    // Arrays: JSON-Vergleich
    if (Array.isArray(oldValue) || Array.isArray(newValue)) {
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }

    // Primitive Werte: direkter Vergleich
    return oldValue !== newValue;
  }

  /**
   * Entfernt Timestamp-Felder aus einem Objekt (rekursiv)
   */
  private removeTimestampsFromObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.removeTimestampsFromObject(item));
    }

    const cleaned: any = {};
    for (const key in obj) {
      // Überspringe Timestamp-Felder
      if (this.EXCLUDED_FIELDS.has(key)) {
        continue;
      }
      cleaned[key] = this.removeTimestampsFromObject(obj[key]);
    }
    return cleaned;
  }

  /**
   * Formatiert Feldnamen für Anzeige
   */
  getFieldDisplayName(field: string): string {
    const mapping: Record<string, string> = {
      // Hauptdaten
      'name': 'Name',
      'status': 'Status',
      'agreement_valid_from': 'Gültig ab',
      'agreement_valid_until': 'Gültig bis',
      'automatic_renewal': 'Automatische Verlängerung',
      'renewal_interval': 'Verlängerung um',
      'objection_period': 'Widerspruchsfrist',
      'objection_date': 'Widerspruchsdatum',
      'has_objection': 'Widerspruch erfolgt',
      'owner': 'Bearbeiter',
      'parent_id': 'Vorgänger',

      // Konditionen
      'currency_code': 'Währung',
      'annual_bonus_value': 'Jahresbonus',
      'annual_bonus_unit': 'Jahresbonus Einheit',
      'discount_in_kind': 'Naturalrabatt',
      'order_comment': 'Auftragshinweis',
      'comment': 'Bemerkung',
      'final_negotiation_date': 'Finales Verhandlungsdatum',
      'link_to_correspondence': 'Link zum Schriftverkehr',

      // Relationen
      'calculation_parameters': 'Konditionen',
      'price_management_weight_sets': 'Gewichtsstaffeln',
      'companies': 'Kunden',
      'media': 'Medien',
      'contact': 'Verhandlungspartner',
      'advertisingTypes': 'Werbemittelarten',
      'companyPriceManagements': 'Kundenzuordnungen'
    };

    return mapping[field] || field;
  }

  getLogDetail(logId: number): Observable<PriceManagementLog> {
    const queryParams: Record<string, string> = {
      ...expandParams
    };

    return this.get('price-management-logs/' + logId, queryParams);
  }
}
